'use client';

import React from 'react';
import { useParams } from 'next/navigation';
import { PulseButton, PulseIconButton } from 'src/components';
import { faLock } from '@fortawesome/pro-solid-svg-icons';
import { useLogging } from 'src/context/logging';
import { useNotification } from 'src/context/notification';
import { useSendResetPasswordEmailMutation } from 'src/api/redux';

interface PasswordResetButtonProps {
  userId: string;
  variant?: 'regular' | 'compact' | 'icon';
}

const PasswordResetButton = ({
  userId,
  variant = 'regular',
}: PasswordResetButtonProps) => {
  const { companyId } = useParams<{ companyId: string }>();
  const log = useLogging();
  const { setNotification } = useNotification();

  const [sendResetPasswordEmail, { isLoading }] =
    useSendResetPasswordEmailMutation();

  const handleSendPasswordReset = async () => {
    try {
      await sendResetPasswordEmail({ companyId, userId }).unwrap();
      setNotification({
        title: 'Success',
        message: 'Password reset email sent',
        type: 'success',
      });
    } catch (err: any) {
      log.exception('Error sending password reset email', {
        tags: {
          userId,
          error: err,
        },
      });

      setNotification({
        title: 'Error',
        message:
          err?.data?.error_message ||
          err?.message ||
          'Error sending password reset email',
        type: 'error',
      });
    }
  };

  switch (variant) {
    case 'regular': {
      return (
        <PulseButton
          loading={isLoading}
          size="sm"
          variant="outline"
          color="primary"
          onClick={handleSendPasswordReset}
          startDecorator={faLock}
          aria-label="Send password reset email"
          data-testid="password-reset-button"
        >
          Send Password Reset
        </PulseButton>
      );
    }

    case 'compact': {
      return (
        <PulseIconButton
          loading={isLoading}
          size="sm"
          variant="soft"
          color="primary"
          onClick={handleSendPasswordReset}
          icon={faLock}
          tooltip="Send Password Reset"
          aria-label="Send password reset email"
        />
      );
    }

    case 'icon': {
      return (
        <PulseIconButton
          loading={isLoading}
          size="sm"
          // `plain` to match the deactivate and impersonate icons sharing the
          // row-hover action bar; `soft` gave these two a filled background.
          variant="plain"
          onClick={handleSendPasswordReset}
          icon={faLock}
          className="text-neutral-500 hover:text-sky-500"
          tooltip="Send Password Reset"
          aria-label="Send password reset email"
        />
      );
    }

    default:
      return null;
  }
};

export default PasswordResetButton;
